import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Copy, Check, Loader2, ChevronRight, Smartphone, Mail, Building2,
  ShieldCheck, AlertCircle, Eye, EyeOff, Send, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCMS } from '../context/CMSContext';
import { writeClipboard } from '../lib/banks';
import { useOrderPolling } from './useOrderPolling';
import { useNotificationPrompt } from './useNotificationPrompt';
import api from '../api';

const PAYID_TYPE_ICON = { Phone: Smartphone, Email: Mail, ABN: Building2 };

const COPY_LABEL = { payid: 'PayID', amount: 'Amount', reference: 'Reference' };

// Early-return UI for non-pending order states. Returns JSX or null.
const renderStatusGuard = ({ loading, error, order, t }) => {
  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center">
        <Loader2 size={28} className="animate-spin text-red-500 mx-auto" />
      </main>
    );
  }
  if (error || !order) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h1 className="text-2xl font-bold text-[#0a0a0a]">{error || 'Order not found'}</h1>
        <p className="mt-2 text-slate-500">Check the reference link or place a new order.</p>
        <Link to="/" className="inline-block mt-6 px-5 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg">Back to shop</Link>
      </main>
    );
  }
  if (order.status === 'paid' || order.status === 'shipped') {
    return <PaidConfirmation order={order} t={t} />;
  }
  if (order.status === 'cancelled') {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center">
        <AlertCircle size={48} className="text-amber-500 mx-auto" />
        <h1 className="mt-4 text-2xl font-bold text-[#0a0a0a]">This order was cancelled</h1>
        <p className="mt-2 text-slate-500">Please get in touch if you believe this is a mistake.</p>
        <Link to="/" className="inline-block mt-6 px-5 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg">Back to shop</Link>
      </main>
    );
  }
  return null;
};

const performCopy = async (text, kind, setCopyState) => {
  const ok = await writeClipboard(text);
  if (!ok) {
    toast.error('Could not copy — long-press the value to copy manually');
    return;
  }
  setCopyState(kind);
  setTimeout(() => setCopyState(null), 3000);
  toast.success(`${COPY_LABEL[kind] || 'Value'} copied`);
};

const PayPage = () => {
  const { reference } = useParams();
  const { t } = useCMS();
  const { order, loading, error } = useOrderPolling(reference);
  useNotificationPrompt();
  const [revealed, setRevealed] = useState(false);
  const [copyState, setCopyState] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const guard = renderStatusGuard({ loading, error, order, t });
  if (guard) return guard;

  const payidValue = t('payid.value', '');
  const payidDisplay = t('payid.display_name', 'FAT BUSTERS');
  const payidType = t('payid.type', 'Phone');
  const bankName = t('payid.bank_name', '');
  const TypeIcon = PAYID_TYPE_ICON[payidType] || Smartphone;
  const amountStr = order.total.toFixed(2);

  const copy = (text, kind) => performCopy(text, kind, setCopyState);

  const claimPaid = async () => {
    setClaiming(true);
    try {
      await api.post(`/orders/by-ref/${order.reference}/claim-paid`);
      setClaimed(true);
      toast.success("Got it — we'll confirm your order within 24 hours.");
    } catch (err) {
      toast.error(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not notify us — please try again.'
      );
    } finally {
      setClaiming(false);
    }
  };

  // Server marks the order with `payment_claimed_at` on first click; reflect it locally on reload.
  const serverClaimed = !!order.payment_claimed_at;
  const showClaimSuccess = claimed || serverClaimed;

  return (
    <main className="max-w-2xl mx-auto px-5 sm:px-6 py-8 sm:py-12">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/" className="hover:text-red-700">Home</Link>
        <ChevronRight size={13} />
        <span>Pay by PayID</span>
      </div>

      <header className="mt-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={22} className="text-red-600" />
        </div>
        <div>
          <h1 className="text-[#0a0a0a] font-bold text-3xl md:text-4xl tracking-tight">
            Pay <span className="text-red-600">A${amountStr}</span>
          </h1>
          <p className="text-slate-500 mt-2">
            Pay <b>{payidDisplay}</b> by PayID from your bank app.
          </p>
        </div>
      </header>

      {/* PAYMENT DETAILS CARD — the only thing the customer needs */}
      <section className="mt-7 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm" data-testid="payment-details-card">
        <DetailRow
          label="Send PayID to"
          icon={<TypeIcon size={15} className="text-slate-400" />}
          subtitle={bankName ? `Recipient: ${payidDisplay} · ${bankName}` : `Recipient: ${payidDisplay}`}
        >
          <div className="flex items-center gap-2 mt-1">
            <code className="text-lg font-mono font-bold text-[#0a0a0a]" data-testid="payid-value">
              {revealed ? payidValue : maskPayid(payidValue, payidType)}
            </code>
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              className="p-1 rounded text-slate-400 hover:text-red-600"
              aria-label={revealed ? 'Hide PayID' : 'Reveal PayID'}
              data-testid="payid-reveal-btn"
            >
              {revealed ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <CopyButton onClick={() => copy(payidValue, 'payid')} state={copyState === 'payid'} testid="copy-payid-btn" label="Copy PayID" />
        </DetailRow>

        <DetailRow label="Amount">
          <code className="text-lg font-mono font-bold text-[#0a0a0a]" data-testid="amount-value">A${amountStr}</code>
          <CopyButton onClick={() => copy(amountStr, 'amount')} state={copyState === 'amount'} testid="copy-amount-btn" label="Copy amount" />
        </DetailRow>

        <DetailRow
          label="Reference"
          subtitle="This must be entered exactly so we can match your payment to your order."
        >
          <code className="text-lg font-mono font-bold text-red-600" data-testid="reference-value">{order.reference}</code>
          <CopyButton onClick={() => copy(order.reference, 'reference')} state={copyState === 'reference'} testid="copy-reference-btn" label="Copy reference" />
        </DetailRow>
      </section>

      {/* INSTRUCTIONS */}
      <section className="mt-6 bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="instructions-card">
        <div className="text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">How to pay</div>
        <ol className="mt-3 text-sm text-slate-700 space-y-3 list-decimal pl-5 marker:text-red-600 marker:font-bold">
          <li>Open your bank app and tap <b>Pay</b> → <b>PayID</b> (or <b>New PayID transfer</b>).</li>
          <li>Paste the <b>PayID</b> above into the PayID field.</li>
          <li>Enter the <b>amount</b> <span className="font-mono whitespace-nowrap">A${amountStr}</span>.</li>
          <li>Enter the <b>reference</b> <span className="font-mono whitespace-nowrap text-red-700">{order.reference}</span> in the description / reference field.</li>
          <li>Confirm and send. Then tap <b>"I've sent the payment"</b> below to let us know.</li>
        </ol>
      </section>

      {/* CLAIM-PAID ACTION — customer signals they've made the transfer.
          Does NOT mark the order paid — admin still verifies. Just notifies admin. */}
      <section className="mt-6" data-testid="claim-paid-section">
        {showClaimSuccess ? (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-5 sm:p-6 text-center" data-testid="claim-success-card">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <Check size={26} className="text-emerald-700" />
            </div>
            <h3 className="mt-3 text-lg font-bold text-emerald-900">Thanks — we'll be in touch</h3>
            <p className="mt-1.5 text-sm text-emerald-800 max-w-md mx-auto">
              We'll confirm your payment and email your receipt within the next <b>24 hours</b>. Keep an eye on your phone and email.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-emerald-700">
              <Clock size={12} /> Order <span className="font-mono font-bold">{order.reference}</span> · A${amountStr}
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={claimPaid}
            disabled={claiming}
            data-testid="claim-paid-btn"
            className={`w-full font-bold rounded-xl px-6 py-4 text-base flex items-center justify-center gap-2 transition-colors shadow-lg ${
              claiming
                ? 'bg-slate-300 text-slate-500 cursor-wait'
                : 'bg-red-500 hover:bg-red-400 active:bg-red-600 text-white shadow-red-500/20'
            }`}
          >
            {claiming
              ? <><Loader2 size={18} className="animate-spin" /> Sending…</>
              : <><Send size={18} /> I've sent the payment — checkout complete</>
            }
          </button>
        )}
        <p className="mt-2.5 text-[11px] text-slate-400 text-center">
          Only tap once you've completed the PayID transfer in your bank app.
        </p>
      </section>

      <div className="mt-5 inline-flex items-center gap-2 text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded-lg">
        <Loader2 size={12} className="animate-spin text-red-500" />
        Watching for your payment — this page updates automatically.
      </div>

      {/* Order summary */}
      <section className="mt-5 flex items-center justify-between text-sm text-slate-500">
        <span>Order <span className="font-mono text-[#0a0a0a]">{order.reference}</span></span>
        <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold text-[11px]">Pending payment</span>
      </section>

      <section className="mt-4 text-[11px] text-slate-400 text-center">
        Having trouble? <Link to="/contact" className="text-red-600 hover:underline">Contact us</Link> and we'll help.
      </section>
    </main>
  );
};

// ----- Helpers -----
const DetailRow = ({ label, icon, subtitle, children }) => (
  <div className="px-5 sm:px-6 py-5 border-b last:border-b-0 border-slate-100">
    <div className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase text-slate-500">
      {icon}
      {label}
    </div>
    <div className="mt-2 flex items-center justify-between gap-3 flex-wrap">
      <div className="flex flex-col">
        {React.Children.toArray(children).filter((c) => c?.type !== CopyButton)}
        {subtitle && <p className="text-[11px] text-slate-500 mt-1.5 max-w-md">{subtitle}</p>}
      </div>
      {React.Children.toArray(children).find((c) => c?.type === CopyButton)}
    </div>
  </div>
);

const CopyButton = ({ onClick, state, testid, label }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg border transition-colors ${
      state
        ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
        : 'bg-white border-red-200 hover:border-red-400 hover:bg-red-50 text-red-700'
    }`}
  >
    {state ? <><Check size={13} /> Copied</> : <><Copy size={13} /> {label}</>}
  </button>
);

const maskPayid = (val, type) => {
  if (!val) return '';
  if (type === 'Phone') return val.length > 4 ? '••• •••' + val.slice(-4) : val;
  if (type === 'Email') {
    const [name, domain] = val.split('@');
    if (!domain) return val;
    return name[0] + '•••@' + domain;
  }
  return val.length > 4 ? '•'.repeat(val.length - 4) + val.slice(-4) : val;
};

const PaidConfirmation = ({ order, t }) => (
  <main className="max-w-2xl mx-auto px-6 py-20 text-center">
    <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
      <Check size={32} className="text-red-600" />
    </div>
    <h1 className="mt-5 text-3xl font-bold text-[#0a0a0a]">{t('checkout.thanks_title', "Thanks — payment received")}</h1>
    <p className="mt-3 text-slate-500">{t('checkout.thanks_body', "Your order is confirmed. We'll email you a receipt and shipping details.")}</p>
    <div className="mt-6 inline-block bg-white border border-slate-200 rounded-xl p-4 text-left">
      <div className="flex justify-between gap-8 text-sm"><span className="text-slate-500">Reference</span><span className="font-mono text-[#0a0a0a]">{order.reference}</span></div>
      <div className="flex justify-between gap-8 text-sm mt-1.5"><span className="text-slate-500">Total paid</span><span className="font-semibold text-[#0a0a0a]">A${order.total.toFixed(2)}</span></div>
    </div>
    <div className="mt-8">
      <Link to="/" className="inline-block px-5 py-3 bg-red-500 hover:bg-red-400 text-white font-semibold rounded-lg" data-testid="thanks-home-btn">Back to shop</Link>
    </div>
  </main>
);

export default PayPage;
